import { Panel } from '#~/account/components/settings/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useIsAdminOrOwner } from '#~/room/hooks.js';
import { Paths } from '#~/routes/index.js';
import { Avatar } from '@studio/design-system/avatar';
import { Button } from '@studio/design-system/button';
import * as DropdownMenu from '@studio/design-system/dropdown-menu';
import { Icon } from '@studio/design-system/icon';
import { Ellipses } from '@studio/design-system/label';
import { getMenuItemAction } from '@studio/design-system/menu';
import { getFullName } from '@studio/models/user';
import { useLatestFn } from '@studio/react-utils/hooks';
import { Sel, STagNew } from '#~/stag/index.js';
import type { T_Optional } from '@tryiris-ai/js-types';
import { toEnum } from '@tryiris-ai/js-types';
import { type ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'cn';
import type { PanelProps } from './types.js';

const NAV_ITEMS: T_Optional<Record<Panel, string>> = {
  [Panel.Profile]: 'Account Settings',
  [Panel.Password]: 'Security',
  [Panel.Studio]: 'Studio Settings',
  [Panel.Billing]: 'Billing',
  [Panel.Organization]: 'Organization Settings',
  [Panel.HotKeys]: 'Hot Keys',
  [Panel.Controllers]: 'Controllers'
};

/** `<Details>` component. */
export const Details = ({
  className,
  email,
  firstName,
  lastName,
  onSettings,
  profilePhotoUrl,
  ...props
}: PanelProps): ReactElement => {
  const navigate = useNavigate();
  const isAdminOrOwner = useIsAdminOrOwner();

  const { enabled: isOrganizationEnabled, loading } = useFeatureFlag(
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE
  );

  const navItems = useMemo(() => {
    const items = { ...NAV_ITEMS };

    if (!loading && !isOrganizationEnabled) {
      delete items[Panel.Organization];
    }

    if (!isAdminOrOwner) {
      delete items[Panel.Billing];
    }

    return items;
  }, [loading, isOrganizationEnabled, isAdminOrOwner]);

  const handleSignOut = useLatestFn(() => {
    navigate(`/${Paths.logout}`);
  });

  const handleSelect = useLatestFn((event: Event) => {
    const panel = toEnum(Panel, getMenuItemAction(event));

    if (panel) {
      onSettings(panel);
    }
  });

  return (
    <div
      className={twMerge('flex w-[216px] flex-col items-center gap-24 py-16', className)}
      {...props}
    >
      <div className='flex flex-col items-center gap-4'>
        <Avatar className='w-[60px] text-2xl' name={firstName} photo={profilePhotoUrl} />
        <Ellipses
          title={getFullName({ firstName, lastName })}
          className='max-w-[150px] text-md font-demibold'
        >
          {getFullName({ firstName, lastName })}
        </Ellipses>
        <Ellipses title={email} className='max-w-[150px] text-sm text-secondary'>
          {email}
        </Ellipses>
      </div>

      <div className='w-full'>
        {Object.keys(navItems).map((panel) => (
          <DropdownMenu.Item
            action={panel}
            className='h-34 text-md'
            key={panel}
            label={navItems[panel as Panel] ?? ''}
            onSelect={handleSelect}
            {...STagNew(Sel.AccountDropdown, 'nav-item', panel)}
          >
            <Icon className='ml-auto rotate-90 !text-xs' icon='chevron-up' />
          </DropdownMenu.Item>
        ))}
      </div>

      <Button onClick={handleSignOut} {...STagNew(Sel.AccountDropdown, 'sign-out')}>
        Sign Out
      </Button>
    </div>
  );
};
